import { TrendingUp, Users, Clock, ClipboardList, Phone, MessageCircle } from "lucide-react";
import { useSettings } from "../hooks/useSettings";
import { EnquiryForm } from "../components/EnquiryForm";
import Seo from "../components/Seo";

const Sell = () => {
  const { data: s } = useSettings();
  const wa = (s?.whatsapp || "").replace(/[^0-9]/g, "");

  const benefits = [
    { icon: TrendingUp, t: "The right price", d: "We help you price your property competitively based on real local market data." },
    { icon: Users, t: "Genuine buyers", d: "Your property reaches serious, verified buyers and tenants in our network." },
    { icon: Clock, t: "Hassle-free process", d: "From listing to closing, our team handles the legwork and paperwork with you." },
  ];

  const steps = [
    { n: "01", t: "Share details", d: "Fill the form or call us with your property details." },
    { n: "02", t: "Free valuation", d: "We assess and suggest the right asking price." },
    { n: "03", t: "We market it", d: "Your property is listed and shown to matching buyers." },
    { n: "04", t: "Close the deal", d: "We assist with negotiation and closing." },
  ];

  return (
    <div>
      <Seo
        title="Sell Your Property in Jaipur — List with Satyam Real Estate"
        description="Want to sell or rent out your flat, villa, plot or commercial space in Jaipur? List with Satyam Real Estate — right pricing, genuine buyers and a hassle-free process."
      />

      {/* Hero + form */}
      <section className="bg-stone-950 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-light">Sell or rent out with us</span>
            <h1 className="font-heading text-4xl sm:text-5xl mt-4 leading-tight">List your property with Satyam Real Estate</h1>
            <p className="text-stone-300 mt-5 max-w-lg">
              Selling or renting out your flat, villa, plot or commercial space in Jaipur? Share the details and our team will help you find the right buyer at the right price.
            </p>
            <ul className="space-y-5 mt-9">
              {benefits.map((b) => (
                <li key={b.t} className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand/20 text-brand-light shrink-0"><b.icon size={20} /></span>
                  <div>
                    <p className="font-semibold">{b.t}</p>
                    <p className="text-sm text-stone-400 mt-1">{b.d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-9">
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hi, I want to sell/rent out my property.")}`} target="_blank" rel="noreferrer"
                data-testid="sell-whatsapp"
                className="flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity">
                <MessageCircle size={18} /> WhatsApp Us
              </a>
              <a href={`tel:${s?.phone}`} data-testid="sell-call"
                className="flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-5 py-3 text-sm font-semibold hover:bg-white/20 transition-colors">
                <Phone size={18} /> {s?.phone}
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl" data-testid="sell-form">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="text-brand" size={22} />
              <h2 className="font-heading text-2xl text-stone-900">Tell us about your property</h2>
            </div>
            <p className="text-sm text-stone-500 mb-5">Fill in a few details and we'll call you back.</p>
            <EnquiryForm defaultIntent="sell" showIntent />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Simple process</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-2">How selling works</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((st) => (
            <div key={st.n} className="rounded-2xl border border-stone-200 p-6" data-testid={`sell-step-${st.n}`}>
              <p className="font-heading text-4xl text-brand/30">{st.n}</p>
              <p className="font-semibold text-stone-900 mt-2">{st.t}</p>
              <p className="text-sm text-stone-500 mt-1">{st.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Sell;
